// src/fleet-stats/components/InactiveRobotsSection.jsx
//
// One drill-down for every "robot not doing anything" framing, replacing four
// scattered sections: went quiet (7d), offline >7d, powered-on-never-driven,
// and no-activity-in-selected-period. Open state and active tab live in
// FleetStats so the FleetBoard's "offline" links can jump straight here.
import React from "react";
import { PowerOff } from "lucide-react";
import { C } from "../constants.js";
import { DataTable } from "./SharedUI.jsx";
import { fmtNum } from "../utils/format.js";

const robotCol = {
    key: "robotName",
    label: "Robot",
    render: (r) => (
        <div>
            <div className="font-medium">{r.robotName}</div>
            <div className="text-xs" style={{ color: C.grey2 }}>{r.robotId}</div>
        </div>
    ),
};

const TABS = [
    {
        key: "quiet",
        label: "Went quiet (7d)",
        description: "Robots that were driving in the 3 weeks prior but have zero driving in the last 7 days of data. Sorted by prior activity — a busier robot going quiet matters more.",
        emptyMessage: "No robots went quiet in the last 7 days.",
        columns: [
            { key: "client", label: "Client" },
            { key: "siteName", label: "Site" },
            robotCol,
            { key: "baselineKm", label: "Prior 3wk avg km/day", align: "right", render: (r) => fmtNum(r.baselineKm, 2) },
            { key: "baselineHours", label: "Prior avg h/day", align: "right", render: (r) => fmtNum(r.baselineHours, 2) },
        ],
    },
    {
        key: "offline",
        label: "Offline > 7 days",
        description: "Robots with no data at all (no uptime, no driving) for more than 7 days. Most recently lost first — the top of this list is the most actionable.",
        emptyMessage: "No robots offline for more than 7 days.",
        columns: [
            { key: "client", label: "Client" },
            { key: "siteName", label: "Site" },
            robotCol,
            { key: "lastDrove", label: "Last data", render: (r) => r.lastDrove || "—" },
            { key: "daysDark", label: "Days dark", align: "right" },
        ],
    },
    {
        key: "idle",
        label: "On, never driven",
        description: "Robots with uptime in the selected range that have never driven at any point in their history. Waiting for commissioning, misconfigured, or should be powered off.",
        emptyMessage: "No robots with uptime that have never driven.",
        columns: [
            { key: "clients", label: "Client" },
            { key: "sites", label: "Site(s)", render: (r) => <div><div>{r.sites}</div>{r.siteCount > 1 && <div className="text-xs" style={{ color: C.grey2 }}>{r.siteCount} sites</div>}</div> },
            robotCol,
            { key: "uptimeH", label: "Uptime (h)", align: "right", render: (r) => fmtNum(r.uptimeH, 1) },
            { key: "activeDays", label: "Days on", align: "right" },
            { key: "firstSeen", label: "First", render: (r) => r.firstSeen || "—" },
            { key: "lastSeen", label: "Last", render: (r) => r.lastSeen || "—" },
        ],
    },
    {
        key: "period",
        label: "No activity in selected period",
        description: "Robots in scope with zero driving inside the currently selected date range. Follows the date filter, unlike the other tabs.",
        emptyMessage: "All robots in scope have activity in this period.",
        columns: [
            { key: "client", label: "Client" },
            { key: "siteName", label: "Site" },
            robotCol,
        ],
    },
];

export function InactiveRobotsSection({ data, open, setOpen, activeTab, setActiveTab, onSelectRobot, sectionRef }) {
    const counts = {
        quiet: data.quiet.length,
        offline: data.offline.length,
        idle: data.idle.length,
        period: data.period.length,
    };
    const total = counts.quiet + counts.offline + counts.idle;
    const tab = TABS.find((t) => t.key === activeTab) || TABS[0];

    return (
        <div className="mb-8" ref={sectionRef}>
            <button
                onClick={() => setOpen(!open)}
                className="inline-flex items-center gap-2 text-sm font-semibold mb-1 transition-colors"
                style={{ color: C.grey1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
            >
                <PowerOff size={16} />
                {open ? "Hide" : "Show"} inactive &amp; idle robots ({total})
            </button>
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
                Every framing of "robot not doing anything" in one place: recently quiet, fully offline, powered on but never driven, and inactive in the selected period.
            </div>

            {open && (
                <>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {TABS.map((t) => {
                            const isActive = activeTab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                                    style={{
                                        backgroundColor: isActive ? "rgba(255,255,255,0.06)" : "transparent",
                                        color: isActive ? C.offWhite : C.grey2,
                                        border: `1px solid ${isActive ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)"}`,
                                    }}
                                >
                                    {t.label} ({counts[t.key]})
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="text-xs rounded-lg px-3 py-2.5 mb-3 leading-relaxed"
                        style={{ backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", color: C.grey1 }}
                    >
                        {tab.description}
                    </div>

                    <DataTable
                        columns={tab.columns}
                        rows={data[tab.key].map((r) => ({ ...r, _key: r.robotId }))}
                        emptyMessage={tab.emptyMessage}
                        maxVisibleRows={10}
                        onRowClick={(r) => onSelectRobot(r.robotName)}
                    />
                </>
            )}
        </div>
    );
}
